/**
 * Pure helpers that drive the Explore Chats page's URL ↔ state sync.
 *
 * Extracted so the bookkeeping (preserve foreign params, no-op when nothing
 * changed, decide when to hydrate vs. write through) can be exhaustively
 * unit-tested without spinning up the React tree. The component imports these
 * directly; there is no other consumer.
 */

export type ViewMode = "chats" | "feedback";

export interface ExploreUrlState {
  viewMode: ViewMode;
  /**
   * Effective session id to surface in the URL — the component computes this
   * as ``selectedFeedback?.session_id ?? selectedChatId`` so the helper does
   * not need to know which path supplied it.
   */
  selectedSessionId?: string;
  selectedFeedbackId?: string;
}

/**
 * Compute the next URLSearchParams reflecting ``state``, while preserving any
 * params not owned by the Explore page (e.g. the ``tab`` slot Admin.tsx sets
 * to identify the active admin sub-tab).
 *
 * Returns ``null`` if the next params are identical to ``prev``. Callers use
 * this to skip a no-op ``setSearchParams`` call — without that guard the
 * writer effect re-fires every render because ``URLSearchParams`` is a fresh
 * object each time, and effects keyed on its identity would loop.
 */
export function nextExploreUrlParams(
  prev: URLSearchParams,
  state: ExploreUrlState,
): URLSearchParams | null {
  const next = new URLSearchParams(prev);
  next.set("view", state.viewMode);

  if (state.selectedSessionId) {
    next.set("sessionId", state.selectedSessionId);
  } else {
    next.delete("sessionId");
  }
  if (state.selectedFeedbackId) {
    next.set("feedbackId", state.selectedFeedbackId);
  } else {
    next.delete("feedbackId");
  }

  return next.toString() === prev.toString() ? null : next;
}

/**
 * Decide whether the page needs to hydrate state from URL params before it is
 * safe to write back. ``true`` when a deep-link param is present that state
 * has not yet absorbed; ``false`` when the URL is empty (or only carries
 * params owned by other components) and the writer can fire immediately.
 *
 * Used as the initial value of the ``hasHydrated`` flag — see ``ExploreChats``
 * for why writing the URL before async hydration completes would clobber the
 * very ``feedbackId`` we are about to read.
 */
export function shouldHydrateFromUrl(prev: URLSearchParams): boolean {
  return Boolean(prev.get("sessionId") || prev.get("feedbackId"));
}

/**
 * Resolve the effective view mode from URL params. ``feedback`` only when
 * explicitly set; everything else (missing, garbage, capitalised) falls back
 * to ``chats``.
 */
export function viewModeFromUrl(prev: URLSearchParams): ViewMode {
  return prev.get("view") === "feedback" ? "feedback" : "chats";
}
